"use client";

import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, Star, Zap, Shield, Users, Clock, Loader2, RotateCcw } from "lucide-react";
import { useAuth } from "@/contexts/auth-context";
import { useToast } from "@/hooks/use-toast";
import { useMobileSubscription } from "@/hooks/use-mobile-subscription";
import { Capacitor } from "@capacitor/core";
import { trackEvent, AnalyticsEvents } from "@/lib/analytics";

interface SubscriptionDialogProps {
  trigger: React.ReactNode;
}

const features = [
  {
    icon: Zap,
    title: "Personalised Coaching",
    description: "Get personalised sleep advice and insights anytime"
  },
  {
    icon: Shield,
    title: "Advanced Analytics",
    description: "Detailed sleep pattern analysis and recommendations"
  },
  {
    icon: Users,
    title: "Multiple Children",
    description: "Track sleep for all your children in one place"
  },
  {
    icon: Clock,
    title: "24/7 Support",
    description: "Round-the-clock access to sleep coaching"
  }
];

export function SubscriptionDialog({ trigger }: SubscriptionDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);

  // Track subscription dialog open
  useEffect(() => {
    if (isOpen) {
      trackEvent(AnalyticsEvents.SUBSCRIPTION_DIALOG_OPENED);
    }
  }, [isOpen]);
  const { user, isSubscribed, refreshUserProfile } = useAuth();
  const { toast } = useToast();
  const {
    products,
    isLoading,
    isInitialized,
    purchaseProduct,
    restorePurchases
  } = useMobileSubscription(user?.uid);

  const isNativePlatform = Capacitor.isNativePlatform();

  // Dynamically load plans from RevenueCat products
  // If no products are available (e.g., on web or not initialized), show loading or message
  const plans = products.length > 0
    ? products.map((product, index) => {
      // Extract plan name from product title or use tier
      const planName = product.title
        .replace(/Subscription/i, '')
        .replace(/LullabAI/i, '')
        .trim() || product.tier.charAt(0).toUpperCase() + product.tier.slice(1);

      return {
        name: planName,
        price: product.priceString, // Use formatted price from RevenueCat
        period: product.period || 'month',
        popular: index === 0, // First product is popular
        savings: null, // Could calculate savings if multiple tieƒrs
        productId: product.productId,
        description: product.description,
        introPrice: product.introPriceString,
        hasIntroOffer: !!product.introPriceString
      };
    })
    : [];

  // Show loading state if products are being fetched
  const showLoading = isLoading || (isNativePlatform && !isInitialized);

  const handleSubscribe = async (productId: string) => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please log in to subscribe.",
        variant: "destructive"
      });
      return;
    }

    if (!isNativePlatform) {
      toast({
        title: "Mobile App Required",
        description: "Subscriptions are only available in the mobile app. Please download the app from the App Store or Google Play.",
      });
      return;
    }

    if (!isInitialized) {
      toast({
        title: "Not Ready",
        description: "Subscription service is initializing. Please try again in a moment.",
        variant: "destructive"
      });
      return;
    }

    try {
      setSelectedPlan(productId);
      trackEvent(AnalyticsEvents.SUBSCRIPTION_PURCHASE_STARTED, { product_id: productId });
      const transaction = await purchaseProduct(productId, user.uid);

      if (transaction) {
        trackEvent(AnalyticsEvents.SUBSCRIPTION_PURCHASE_COMPLETED, { product_id: productId });
        // Refresh user profile to immediately pick up new subscription status
        await refreshUserProfile();
        setIsOpen(false);
        setSelectedPlan(null);
      } else {
        // Purchase failed but didn't throw - clear selected plan to re-enable button
        setSelectedPlan(null);
      }
    } catch (error) {
      console.error('Subscription error:', error);
      trackEvent(AnalyticsEvents.SUBSCRIPTION_PURCHASE_FAILED, { product_id: productId });
      setSelectedPlan(null);
    }
  };

  const handleRestorePurchases = async () => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please log in to restore purchases.",
        variant: "destructive"
      });
      return;
    }

    if (!isNativePlatform) {
      toast({
        title: "Mobile App Required",
        description: "Purchase restoration is only available in the mobile app.",
        variant: "destructive"
      });
      return;
    }

    try {
      await restorePurchases(user.uid);
      trackEvent(AnalyticsEvents.SUBSCRIPTION_RESTORED);
    } catch (error) {
      console.error('Restore purchases error:', error);
    }
  };

  if (isSubscribed) {
    return (
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          {trigger}
        </DialogTrigger>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Already Subscribed</DialogTitle>
            <DialogDescription>
              You already have an active subscription. Enjoy your premium features!
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Star className="h-5 w-5 text-yellow-500" />
            Upgrade to Premium
          </DialogTitle>
          <DialogDescription>
            Unlock 24/7 AI sleep coaching, custom schedules, and advanced analytics for £4.99/month
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Features */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {features.map((feature) => (
              <div key={feature.title} className="flex items-start gap-3">
                <feature.icon className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <h4 className="font-medium">{feature.title}</h4>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Plans */}
          {showLoading ? (
            <div className="flex justify-center items-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Loading subscription plans...</span>
            </div>
          ) : plans.length === 0 ? (
            <div className="flex justify-center">
              <div className="w-full max-w-sm">
                <Card className="relative ring-2 ring-primary">
                  <Badge className="absolute -top-2 left-1/2 transform -translate-x-1/2">
                    Monthly
                  </Badge>
                  <CardHeader>
                    <CardDescription className="text-center">
                      <span className="text-2xl font-bold">£4.99</span>
                      <span className="text-muted-foreground">/month</span>
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-xs text-center text-muted-foreground">
                      Unlimited 24/7 AI Sleep Coaching, schedule generator, and unlimited children.
                    </p>
                    <p className="text-xs text-center font-medium text-primary">
                      {isNativePlatform
                        ? "Connecting to app store..."
                        : "Subscriptions are available in the mobile app."}
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          ) : (
            <div className="flex justify-center">
              <div className="w-full max-w-sm space-y-4">
                {plans.map((plan) => (
                  <Card key={plan.name} className={`relative ${plan.popular ? 'ring-2 ring-primary' : ''}`}>
                    {plan.popular && (
                      <Badge className="absolute -top-2 left-1/2 transform -translate-x-1/2">
                        Subscribe
                      </Badge>
                    )}
                    <CardHeader>
                      <CardDescription className="text-center">
                        {plan.hasIntroOffer ? (
                          <div className="flex flex-col items-center">
                            <div className="flex items-baseline gap-1">
                              <span className="text-2xl font-bold">{plan.introPrice}</span>
                              <span className="text-muted-foreground">for first month</span>
                            </div>
                            <div className="text-sm text-muted-foreground mt-1">
                              Then {plan.price}/{plan.period}
                            </div>
                            <Badge variant="secondary" className="mt-2 bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-900 dark:text-green-100">
                              Introductory Offer
                            </Badge>
                          </div>
                        ) : (
                          <>
                            <span className="text-2xl font-bold">{plan.price}</span>
                            <span className="text-muted-foreground">/{plan.period}</span>
                            {plan.savings && (
                              <Badge variant="secondary" className="ml-2">
                                {plan.savings}
                              </Badge>
                            )}
                          </>
                        )}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Button
                        className="w-full"
                        onClick={() => handleSubscribe(plan.productId)}
                        disabled={isLoading || !isInitialized || selectedPlan === plan.productId}
                      >
                        {selectedPlan === plan.productId ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Check className="h-4 w-4 mr-2" />
                        )}
                        {selectedPlan === plan.productId ? 'Processing...' : `Subscribe · ${plan.price || '£4.99'}/month`}
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Restore Purchases Button */}
          {isNativePlatform && (
            <div className="text-center">
              <Button
                variant="outline"
                onClick={handleRestorePurchases}
                disabled={isLoading || !isInitialized}
                className="w-full sm:w-auto"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Restore Purchases
              </Button>
            </div>
          )}

          <div className="text-center text-sm text-muted-foreground">
            <p>£4.99/month • Cancel anytime in store settings</p>
            {!isNativePlatform && (
              <p>Available on iOS and Android.</p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
